/**
 * Persist a small piece of UI state to localStorage (RM-118). SSR-safe (reads
 * lazily), swallows quota/parse errors, and mirrors the `useState` setter shape
 * (value or updater fn). Used for the jobs-table column config.
 */
import { useCallback, useState } from 'react'

export function useLocalStorage<T>(
  key: string,
  initial: T,
): readonly [T, (next: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    if (typeof window === 'undefined') return initial
    try {
      const raw = window.localStorage.getItem(key)
      return raw === null ? initial : (JSON.parse(raw) as T)
    } catch {
      return initial
    }
  })

  const set = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValue((prev) => {
        const resolved =
          typeof next === 'function' ? (next as (p: T) => T)(prev) : next
        try {
          window.localStorage.setItem(key, JSON.stringify(resolved))
        } catch {
          // ignore quota / privacy-mode failures
        }
        return resolved
      })
    },
    [key],
  )

  return [value, set] as const
}
